import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import { Column } from '@repo/ui/data-table'
import { ChevronDownIcon, ChevronRightIcon, ChevronUpDownIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'

import { ReviewColumnAggregate } from '../../../api/end-of-journey-reviews/centre/types'
import {
  EndOfJourneyReviewsSummaryResponse,
  SummaryCountryGroup
} from '../../../api/end-of-journey-reviews/summary/types'
import ReviewRatingPill, {
  receptionStatusVariant
} from '../../../components/pages/ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'
import { Paths } from '../../../lib/constants/path'
import { constructOlCenterPath } from '../../ols/center/utils/functions'
import { formatSummaryFigure, SummaryRow } from './utils'

type Options = {
  t: TFunction<AvailableLanguages, undefined>
  // hides the checkboxes and the sort toggle (print)
  readOnly?: boolean
  expanded: Set<string>
  selectedIds: Set<number>
  countryOrder: 'asc' | 'desc'
  onToggleCountry: (countryKey: string) => void
  onSelect: (centreIds: number[], selected: boolean) => void
  onToggleCountryOrder: () => void
}

const formatDate = (date: string | null) => (date ? DateFormatter.DDMMYYYY(date) : '-')

const isSelected = (ids: number[], selectedIds: Set<number>) =>
  ids.length > 0 && ids.every((id) => selectedIds.has(id))

const findAggregate = (aggregates: ReviewColumnAggregate[], code: ReviewColumnAggregate['code']) =>
  aggregates.find((aggregate) => aggregate.code === code)

// The toggle sits on the Badge itself: the Badge stops the click, so a wrapping button never gets it
const CountryChip = ({
  group,
  expanded,
  onToggle
}: {
  group: SummaryCountryGroup
  expanded: boolean
  onToggle?: () => void
}) => {
  const Icon = expanded ? ChevronDownIcon : ChevronRightIcon
  return (
    <Badge
      variant="primary"
      text={`${group.country?.name ?? '-'} (${group.centres.length})`}
      icon={<Icon color={tailwindColors.primary.DEFAULT} />}
      className="whitespace-nowrap"
      role={onToggle ? 'button' : undefined}
      tabIndex={onToggle ? 0 : undefined}
      aria-expanded={onToggle ? expanded : undefined}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (!onToggle || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        onToggle()
      }}
    />
  )
}

/** Columns of the summary: checkbox, Pays, Centre, insc. and one column per column code. */
export const getSummaryColumns = (
  data: EndOfJourneyReviewsSummaryResponse,
  {
    t,
    readOnly,
    expanded,
    selectedIds,
    countryOrder,
    onToggleCountry,
    onSelect,
    onToggleCountryOrder
  }: Options
): Column<SummaryRow>[] => {
  const allCentreIds = data.countries.flatMap((group) => group.centres.map(({ id }) => id))

  const selectColumn: Column<SummaryRow> = {
    header: '',
    accessorKey: 'id',
    headerInfo: (
      <Checkbox
        checked={isSelected(allCentreIds, selectedIds)}
        disabled={allCentreIds.length === 0}
        onChange={(checked) => onSelect(allCentreIds, checked)}
      />
    ),
    cell: ({ row }) => {
      const { original } = row
      if (original.kind !== 'country' && original.kind !== 'centre') return null
      const ids =
        original.kind === 'country' ? original.group.centres.map(({ id }) => id) : [original.id]

      return (
        <div onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={isSelected(ids, selectedIds)}
            onChange={(checked) => onSelect(ids, checked)}
          />
        </div>
      )
    }
  }

  return [
    ...(readOnly ? [] : [selectColumn]),
    {
      header: t('end.of.stay.appreciations.summary.columns.country'),
      accessorKey: 'kind',
      headerInfo: readOnly ? undefined : (
        <button
          type="button"
          aria-label={t(
            countryOrder === 'asc'
              ? 'end.of.stay.appreciations.summary.sort.desc'
              : 'end.of.stay.appreciations.summary.sort.asc'
          )}
          onClick={onToggleCountryOrder}
        >
          <ChevronUpDownIcon color={tailwindColors.grey.DEFAULT} />
        </button>
      ),
      cell: ({ row }) => {
        const { original } = row
        if (original.kind === 'general' && original.first)
          return (
            <Typography variant="label-semibold" color={tailwindColors.secondary.DEFAULT}>
              {t('end.of.stay.appreciations.summary.general.total')}
            </Typography>
          )
        if (original.kind !== 'country') return null

        return (
          <CountryChip
            group={original.group}
            expanded={readOnly || expanded.has(original.countryKey)}
            onToggle={readOnly ? undefined : () => onToggleCountry(original.countryKey)}
          />
        )
      }
    },
    {
      header: t('end.of.stay.appreciations.summary.columns.centre'),
      accessorKey: 'label' as Column<SummaryRow>['accessorKey'],
      cell: ({ row }) => {
        const { original } = row
        if (original.kind === 'general' || original.kind === 'subtotal')
          return (
            <Typography
              variant="label-semibold"
              color={original.kind === 'general' ? tailwindColors.secondary.DEFAULT : undefined}
              className="whitespace-nowrap"
            >
              {original.label}
            </Typography>
          )
        if (original.kind !== 'centre') return null

        const { centre } = original
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Link
              to={constructOlCenterPath(Paths.OL_CENTER_END_OF_STAY_APPRECIATIONS, centre.id)}
              className="underline text-blue"
              onClick={(event) => event.stopPropagation()}
            >
              {centre.name}
            </Link>
            <Badge
              variant="grey"
              text={`${formatDate(centre.startDate)} - ${formatDate(centre.endDate)}`}
            />
            <Badge
              variant={receptionStatusVariant[centre.receptionStatus]}
              text={`${centre.received}/${centre.enrolled}`}
            />
          </div>
        )
      }
    },
    {
      header: t('end.of.stay.appreciations.summary.columns.enrolled'),
      accessorKey: 'enrolled' as Column<SummaryRow>['accessorKey'],
      cell: ({ row }) => {
        const { original } = row
        if (original.kind === 'centre')
          return <ReviewRatingPill variant="secondaryBlue" text={`${original.centre.enrolled}`} />
        if (original.kind === 'subtotal')
          return (
            <ReviewRatingPill
              variant="secondaryBlue"
              text={original.figure === 'average' ? `${original.group.enrolled}` : '-'}
            />
          )
        return null
      }
    },
    ...data.columns.map(
      ({ code, shortLabel }): Column<SummaryRow> => ({
        header: shortLabel,
        accessorKey: `aggregates.${code}` as Column<SummaryRow>['accessorKey'],
        cell: ({ row }) => {
          const { original } = row
          if (original.kind === 'general')
            return (
              <ReviewRatingPill
                variant="warning"
                text={formatSummaryFigure(
                  findAggregate(data.general.aggregates, code),
                  original.figure
                )}
              />
            )
          if (original.kind === 'subtotal')
            return (
              <ReviewRatingPill
                text={formatSummaryFigure(
                  findAggregate(original.group.aggregates, code),
                  original.figure
                )}
              />
            )
          if (original.kind === 'centre')
            return (
              <ReviewRatingPill
                text={formatSummaryFigure(
                  findAggregate(original.centre.aggregates, code),
                  'average'
                )}
              />
            )
          return null
        }
      })
    )
  ]
}
